import ContentConfig from '../../config/ContentConfig';
import logger from '../../log';
import ObjectCache from './ObjectCache';
import SourceStack from './SourceStack';
import kernelConfig from './KernelConfig';
import Node from './Node';
import emitConnectionChanged from '../../common/emitConnectionChanged';
import getPeerConfig from './getPeerConfig';
import PeerConfig from './PeerConfig';
import prependProtocol from '../../common/utils/prependProtocol';
import { PENDING_CLUSTER, PlayType } from '../../common/constants';
import isWSAuthErrorCode from './isWSAuthErrorCode';
import SignalConnection, { RpcType, PingAck } from './SignalBus';
import PeerConnection from './PeerConnection';
import { ChannelError, getConstraints } from './PeerChannel';
import Metrics from './Metrics';
import getKernelModule from './getKernelModule';
import { MESSAGE_TYPES } from './MessageEncodingDecoding';
import { NodeMessageTypes, AgentEventType } from './NodeMessages';
import EventEmitter from '../../common/EventEmitter';
import { InternalEvents } from '../../events';
import nodeUpdateEvent from '../../events/NodeUpdateEvent';
import { KernelNode } from './KernelNode';
import RetryPeerTracker from './RetryPeerTracker';
import { AgentCodes } from './AgentCodes';
import getKollectiveHeaders from '../../common/utils/getKollectiveHeaders';
import { ConnectionChangeState } from '@kollective-frontend/ksdk-api';
import { sendSessionDiagnostic } from '../../api/diagnostics/sendDiagnostic';
import { DiagnosticCode } from '../../api/diagnostics/constants';
import { generateSessionDiagnosticArgs } from '../../api/diagnostics/GenerateDiagnostic';

const log = logger.getLogger(__filename);

const { performance } = window;

export const FETCH_TIMEOUT = 5000;

function noop() {}

function getDelay(playType, playbackConfig) {
  const { live, vod } = playbackConfig || {};
  switch (playType) {
    case PlayType.LIVE:
      return live ? live.delay : 0;
    case PlayType.VOD:
      return vod ? vod.delay : 0;
    default:
      log.warn(`Unknown playtype: ${playType}. Using LIVE delay`);
      return live ? live.delay : 0;
  }
}

// Close Event Codes for Webrtc Agent
// 4001: standard call to close a peer connection.
// 4002: missing content (content, content.id, or content.items)
// 4003: timeout
// 4004: closed based on an error.

// Wrappers for emitConnectionChanged, so that we don't have to repeatedly add
// the caller and the type.
const sendConnectionEvent = (state, info) => {
  sendCallConnectionEvent(undefined, state, info);
};

const sendCallConnectionEvent = (caller, state, info) => {
  info = info || {};
  if (caller) {
    info.caller = caller;
  }
  emitConnectionChanged({ state, type: 'webrtc-agent', ...info });
};

/**
 * content: { id: string; tenantId: string; url: string; items: Item[] };
 */
export default class Agent {
  constructor(
    localState,
    content,
    playType,
    sourceType,
    createObjectId,
    callbacks
  ) {
    this.config = ContentConfig.getInstance();
    this.eventEmitter = EventEmitter.getInstance();
    this.retryTracker = new RetryPeerTracker(EventEmitter.getInstance());
    this.localState = localState;
    this.createObjectId = createObjectId;
    const { id, tenantId, items } = content;
    this.tenantId = tenantId;
    this.content = { id, items };
    this.playType = playType;
    this.node = Node.create(this.tenantId, this.content.id);
    this.initialized = false;
    this.appName = this.config.app;
    // this.module = module;
    const { cache } = this.config.peering || {};
    this.cache = new ObjectCache({ ...cache, id: this.content.id });
    // go ahead and initialize the cache
    this.cache.initialize();
    this.fetchCallbacks = new Map();
    this.metrics = new Metrics(
      this.node,
      this.cache,
      this.appName,
      this.playType,
      this.eventEmitter
    );
    this.signalHost = this.config.sgh;
    this.configUrl = new URL(
      '/api/rtc/node',
      prependProtocol(this.signalHost)
    ).toString();
    this.setupDelay = getDelay(this.playType, this.config.playback);

    this.isShutdown = false;
    this.kernelNode = undefined;
    this.kernelConfig = kernelConfig(this.config.peering.node);

    this.localities = this.config.localities;
    callbacks.onpeerconfigured = callbacks.onpeerconfigured || noop;
    callbacks.oninitialized = callbacks.oninitialized || noop;
    callbacks.onshutdown = callbacks.onshutdown || noop;

    // handlers
    this.onpeerconfigured = peerConfig => {
      const { nodeId, clusterId } = peerConfig.node;
      const data = { node: { nodeId, clusterId } };
      sendConnectionEvent(ConnectionChangeState.PEER_CONFIGURED, data);
      callbacks.onpeerconfigured(peerConfig);
    };
    this.oninitialized = () => {
      sendConnectionEvent(ConnectionChangeState.INITIALIZED);
      callbacks.oninitialized();
    };
    //this.onshutdown = callbacks.onshutdown || noop;
    this.onshutdown = () => {
      sendConnectionEvent(ConnectionChangeState.SHUTDOWN);
      callbacks.onshutdown();
    };

    this.peerConnections = new Map();
    this.waitingPings = new Map();
    this.waitingAcks = new Map();

    this.source = new SourceStack({
      items,
      sourceType,
      playType: this.playType,
      config: ContentConfig.getInstance(),
      sdkVer: this.localState.play.sdkConfig.version,
      sessionToken: this.localState.reporter.sessionToken,
      createObjectId: this.createObjectId,
      tenantId: this.tenantId,
      contentId: this.content.id
    });

    this.kernelPromise = getKernelModule(this.config.peering.wasmUrl);

    this.attachmentRetries = 0;
    this.maxAttachmentRetries = this.config.peering.node.maxAttachmentRetries;
    this.attachmentRetryTimer = this.config.peering.node.peerFetchTimeout;

    // Setup Events
    this.events = new Map();
    this.events.set(InternalEvents.PENDING_OBJECT, ({ type, data }) => {
      if (!data && !data.objectId) {
        log.debug(`received invalid data for event ${type}`, data);
        return;
      }
      this.notifyPending(data.objectId);
    });
  }

  bindEvents() {
    this.events.forEach((value, key) => {
      this.eventEmitter.addEventListener(key, value);
    });
  }

  unbindEvents() {
    this.events.forEach((value, key) => {
      this.eventEmitter.removeEventListener(key, value);
    });
  }

  currentPeerConnections() {
    return this.peerConnections.size;
  }

  // content: { id: string; url: string; items: Item[] };
  _configure(peerConfig) {
    this.peerConfig = peerConfig;
    if (this.peerConfig) {
      this.node = this.peerConfig.node;
      this.onpeerconfigured(this.peerConfig);
    } else {
      throw Error('Invalid peer configuration');
    }
  }

  /**
   * Loads the asynchronous portions of the Agent's members.
   * @returns Promise that resolves when the agent is finished loading
   */
  async _setupPeer() {
    if (!this.peerConfig) {
      try {
        const { contentId, playGuid, sdkConfig, tenantId } =
          this.localState.play;
        const noUAHeaders = true;
        const args = { contentId, playGuid, sdkConfig, tenantId, noUAHeaders };
        const headers = getKollectiveHeaders(args);
        const sessionToken = this.localState.reporter.sessionToken;
        const { configUrl: url, node, signalHost } = this;
        const config = await getPeerConfig(url, sessionToken, node, headers);
        const peerConfig = new PeerConfig(config, signalHost, sessionToken);
        this._configure(peerConfig);
      } catch (error) {
        const info = { error, message: 'Failed to setup peerConfig' };
        sendCallConnectionEvent(this, ConnectionChangeState.CLOSE, info);
        log.error('Failed to setup peerConfig', error);
        return Promise.reject(error);
      }
    }
    if (!this.kernelModule || typeof this.kernelModule === 'undefined') {
      try {
        this.kernelModule = await this.kernelPromise;
        // Release the promise reference.
        this.kernelPromise = undefined;
      } catch (error) {
        log.error('Failed to load kernel module: ', error);

        const diag = generateSessionDiagnosticArgs({
          code: DiagnosticCode.FAILED_LOAD_PEERING_KERNEL,
          from: 'webrtc-agent',
          state: this.localState
        });
        await sendSessionDiagnostic(diag);
        return Promise.reject(error);
      }
    }
    let locality = '';
    if (Array.isArray(this.localities) && this.localities.length > 0) {
      locality = this.localities[this.localities.length - 1];
    }
    const nodeConfig = {
      ...this.node,
      ...this.kernelConfig
    };
    const handleCallback = nodeMessage => this.handleNodeMessage(nodeMessage);
    const binding = new this.kernelModule.NodeBinding(
      handleCallback,
      nodeConfig,
      log,
      locality
    );
    this.kernelNode = new KernelNode(binding);
    this.cache.onEvict(key => {
      this.notifyEvicted(key);
    });
    // peer config exists.
    return SignalConnection.init(this.peerConfig, {
      onopen: this.signalOpen,
      onreconnect: this.signalReconnect,
      onclose: this.signalClose
    });
  }

  _peerClosed(peer, reason) {
    const data = { closeEvent: { code: 4001, reason } };
    sendConnectionEvent(ConnectionChangeState.CLOSE, data);
    this.pushMessage(NodeMessageTypes.CLOSE_PEER, {
      from: peer.nodeId,
      to: this.node.nodeId,
      reason
    });
    this.peerConnections.delete(peer.nodeId);
  }

  /**
   * Initialize the Agent. initialized should be true when this completes.
   */
  init() {
    if (this.initialized) {
      return Promise.resolve();
    }
    // bind event handlers
    this.bindEvents();
    // if the object is null or it has no id field return, don't set up.
    if (!this.content || !this.content.id || !this.content.items) {
      const data = { closeEvent: { code: 4002, reason: 'missing content' } };
      sendConnectionEvent(ConnectionChangeState.ERROR, data);
      return Promise.reject(new Error('Invalid Configuration. No content set'));
    } else {
      // we don't return from here because the websocket may fail to open for some duration.
      // If that's the case, waiting for this promise to resolve could cause failure to do playback.
      // Shutdown will happen if setupPeer fails.
      try {
        return this._setupPeer().then(
          () => {
            this.initialized = true;
            this.oninitialized();
            try {
              this.kernelNode.initiateAttachment();
            } catch (e) {
              log.warn('Could not start the kernelNode. ', e);
            }
            if (this.setupDelay > 0) {
              return new Promise(resolve => {
                const interval = setInterval(() => {
                  let knode = {};
                  try {
                    knode = this.kernelNode.getNode() || {};
                  } catch (e) {
                    log.warn('BindingError: could not get node ', e);
                  }
                  const attachment = knode.attachment || {};
                  if (attachment.activeCluster) {
                    clearInterval(interval);
                    resolve('ready');
                  }
                }, 100);

                setTimeout(() => resolve('full delay'), this.setupDelay);
              });
            } else {
              return Promise.resolve('no delay');
            }
          },
          error => {
            const info = { error, message: 'error from Agent.setupPeer' };
            sendConnectionEvent(ConnectionChangeState.ERROR, info);
            this.close(error);
            return Promise.reject(error);
          }
        );
      } catch (e) {
        const info = { error: e, message: 'error from Agent' };
        sendConnectionEvent(ConnectionChangeState.ERROR, info);
        return Promise.reject(e);
      }
    }
  }

  signalClose = closeEvent => {
    const isAuthError = isWSAuthErrorCode(closeEvent);
    const tag = isAuthError ? ' Auth Error' : '';
    const info = { closeEvent, message: `closed from signal${tag}` };
    sendCallConnectionEvent(this, ConnectionChangeState.CLOSE, info);
    if (isAuthError) {
      log.error('Failed to authenticate websocket.', closeEvent);
      this.close(closeEvent);
    }
  };

  signalOpen = () => {
    const hasSucceeded = true;
    sendCallConnectionEvent(this, ConnectionChangeState.OPEN, { hasSucceeded });
    const localAddress = this.peerConfig.eventBusLocalAddress;
    const peerHandler = this.peerHandler.bind(this);
    SignalConnection.registerHandler(localAddress, peerHandler);
  };

  signalReconnect = () => {
    sendConnectionEvent(ConnectionChangeState.RECONNECT);
    // rebind handlers
    const localAddress = this.peerConfig.eventBusLocalAddress;
    const peerHandler = this.peerHandler.bind(this);
    SignalConnection.registerHandler(localAddress, peerHandler);
  };

  peerHandler(err, msg) {
    if (err) {
      log.error(err);
    } else {
      log.info(`peerhandler, ${JSON.stringify(msg)}`);

      const { type, payload } = msg.body;
      const { from } = payload;
      if (from.nodeId === this.node.nodeId) {
        log.error('received a message from self');
      }
      let pc;
      switch (type) {
        case RpcType.PEER_OFFER:
          SignalConnection.tick();
          log.info(`creating a connection for peer: ${from.nodeId}`);
          this.createIncomingPeerConnection(from, payload.offer);
          break;
        case RpcType.PEER_ANSWER:
          SignalConnection.tick();
          pc = this.getPeerConnection(from);
          if (pc) {
            pc.setRemoteAnswer(payload.answer);
          }
          break;
        case RpcType.ICE_CANDIDATE:
          SignalConnection.tick();
          pc = this.getPeerConnection(from);
          if (pc) {
            pc.addRemoteCandidate(payload.candidate);
          }
          break;
        case RpcType.PING_ACK:
          SignalConnection.tick();
          if (payload.pingAck === PingAck.PING) {
            this.waitingAcks.set(from.nodeId, response => {
              SignalConnection.sendAck(payload.from, this.node, response);
            });
            this.pushMessage(NodeMessageTypes.PEER_OFFER, payload.data);
            setTimeout(() => {
              if (this.waitingAcks.has(from.nodeId)) {
                log.warn(
                  'Did not receive response from peering kernel after delivering offer'
                );
                this.waitingAcks.delete(from.nodeId);
              }
            }, 1000);
          } else if (
            payload.pingAck === PingAck.ACK &&
            this.waitingPings.has(from.nodeId)
          ) {
            this.waitingPings.get(from.nodeId)(payload.data);
            this.waitingPings.delete(from.nodeId);
          }
          break;
        default:
          log.error(`handled unexpected message type: ${payload.type}`);
          break;
      }
    }
  }

  manageAttachment(msg) {
    if (msg.body.status !== 200) {
      this.attachmentRetries++;
      if (this.attachmentRetries >= this.maxAttachmentRetries) {
        setTimeout(() => {
          this.kernelNode.initiateAttachment();
        }, this.attachmentRetryTimer);
        this.attachmentRetries = 0;
        this.attachmentRetryTimer *= 2;
      } else {
        this.kernelNode.initiateAttachment();
      }
    }
  }

  handleNodeMessage(msg) {
    log.trace('Received message from node: ', msg);
    const now = window.performance.now();
    switch (msg.type) {
      case NodeMessageTypes.SPINUP_TIME:
        break;
      case NodeMessageTypes.ATTACHMENT_STATUS:
        this.manageAttachment(msg);
        break;
      case NodeMessageTypes.TRANSFER_RECORD:
        this.metrics.addTransferRecord(msg.body);
        break;
      case NodeMessageTypes.CONTENT_NOTIFICATION_RECORD:
        this.metrics.addContentNotificationRecord(msg.body);
        break;
      case NodeMessageTypes.CONNECTION_RECORD:
        this.metrics.addConnectionRecord(msg.body);
        break;
      case NodeMessageTypes.PEER_NOTIFICATION_RECORD:
        this.metrics.addPeerNotificationRecord(msg.body);
        break;
      case NodeMessageTypes.PEER_REQUEST_RECORD:
        this.metrics.addPeerRequestRecord(msg.body);
        break;
      case NodeMessageTypes.PEER_STATE_RECORD:
        this.metrics.addPeerStateRecord(msg.body);
        break;
      case NodeMessageTypes.DETACH_RECORD:
        this.metrics.addClusterDetachRecord(msg.body);
        break;
      case NodeMessageTypes.ATTACHMENT_REQUEST_RECORD:
        this.metrics.addAttachmentRequestRecord(msg.body);
        break;
      case NodeMessageTypes.COORD_CLUSTER_DETACH:
      case NodeMessageTypes.NODE_UPDATE:
      case NodeMessageTypes.ATTACHMENT_REQUEST:
      case NodeMessageTypes.CLUSTER_LIST_REQUEST:
        this.handleSignalMessage(msg);
        break;
      case NodeMessageTypes.INGEST_REQUEST:
        this.source.sourceContent(msg.body.from, msg.body.metadata);
        break;
      case NodeMessageTypes.FETCH_RESPONSE:
        // Check for a pending fetch and resolve it
        if (this.fetchCallbacks.has(msg.body.objectId)) {
          this.fetchCallbacks.get(msg.body.objectId)(msg.body);
          this.fetchCallbacks.delete(msg.body.objectId);
        }
        break;
      case NodeMessageTypes.CLOSE_PEER:
        // Close a peer connection
        if (msg.body.to && this.peerConnections.has(msg.body.to)) {
          // Send a peer closed message to the other side as a courtesy and to enable reporting
          this.peerConnections
            .get(msg.body.to)
            .sendMessage(MESSAGE_TYPES.NODE_MESSAGE, msg);
          this.peerConnections
            .get(msg.body.to)
            .close(
              new ChannelError('Kernel is closing connection', msg.body.reason)
            );
          this.peerConnections.delete(msg.body.to);
        }
        break;
      case NodeMessageTypes.TIMER:
        // Start a new timer
        if (msg.body.when > now) {
          setTimeout(() => {
            this.pushMessage(NodeMessageTypes.TIMEOUT, {
              id: msg.body.id
            });
          }, msg.body.when - now);
        } else {
          this.pushMessage(NodeMessageTypes.TIMEOUT, {
            id: msg.body.id
          });
        }
        break;
      case NodeMessageTypes.PEER_OBJECT_RESPONSE:
        // Send all other messages to the appropriate peer
        if (msg.body.to && this.peerConnections.has(msg.body.to)) {
          this.peerConnections
            .get(msg.body.to)
            .sendPeerObjectResponse(
              msg.body.objectId,
              msg.body.status,
              msg.body.metadata
            );
        } else {
          log.warn(
            'No peer ID or peer connection available, cannot send content response',
            msg
          );
        }
        break;
      case NodeMessageTypes.PEER_OFFER:
        // Open a new peer connection (outgoing)
        this.createOutgoingPeerConnection(msg.body);
        break;
      case NodeMessageTypes.PEER_RESPONSE:
        if (this.waitingAcks.has(msg.body.to.nodeId)) {
          this.waitingAcks.get(msg.body.to.nodeId)(msg.body);
          this.waitingAcks.delete(msg.body.to.nodeId);
        } else {
          log.warn(
            'No acks waiting for response to ' +
              msg.body.to.nodeId +
              ', cannot send peer response',
            msg
          );
        }
        break;
      case NodeMessageTypes.FINISHED:
        log.debug('Kernel indicates peering is closed');
        break;
      default:
        // Send all other messages to the appropriate peer
        if (msg.body.to && this.peerConnections.has(msg.body.to)) {
          this.peerConnections
            .get(msg.body.to)
            .sendMessage(MESSAGE_TYPES.NODE_MESSAGE, msg);
        } else {
          log.warn('No peer ID or peer connection available', msg);
        }
        break;
    }
  }

  handleSignalMessage(message) {
    SignalConnection.tick();
    if (message.type === NodeMessageTypes.ATTACHMENT_REQUEST) {
      const nextResponse = resp => {
        resp.status = 200;
        resp.final = false;
        this.pushMessage(NodeMessageTypes.ATTACHMENT_RESPONSE, resp);
      };
      const errorHandler = err => {
        log.warn('Error sending attachment request to signal: ', err.message);
        this.pushMessage(NodeMessageTypes.ATTACHMENT_RESPONSE_COMPLETE, {
          status: 500
        });
      };
      const completeHandler = () => {
        this.pushMessage(NodeMessageTypes.ATTACHMENT_RESPONSE_COMPLETE, {
          status: 200
        });
      };
      SignalConnection.request(message.body).subscribe({
        next(resp) {
          nextResponse(resp);
        },
        error(err) {
          errorHandler(err);
        },
        complete() {
          completeHandler();
        }
      });
    } else if (message.type === NodeMessageTypes.NODE_UPDATE) {
      const { node } = message.body;
      SignalConnection.notify(node);
      this.eventEmitter.emit(InternalEvents.NODE_UPDATE, nodeUpdateEvent(node));
    } else if (
      message.type === NodeMessageTypes.COORD_CLUSTER_DETACH &&
      message.body.node.attachment
    ) {
      SignalConnection.detach({
        nodeId: message.body.node.id,
        clusterId: message.body.node.attachment.activeCluster.id
      });
    } else if (message.type === NodeMessageTypes.CLUSTER_LIST_REQUEST) {
      SignalConnection.listClusters(message.body.node)
        .then(r => {
          r.status = 200;
          log.debug('cluster list response: ', r);
          this.pushMessage(NodeMessageTypes.CLUSTER_LIST_RESPONSE, r);
        })
        .catch(err => {
          log.warn(
            'Error sending cluster list request to signal: ',
            err.message
          );
          this.pushMessage(NodeMessageTypes.CLUSTER_LIST_RESPONSE, {
            status: 500
          });
        });
    } else {
      log.warn('Unknown message sent to signal, ignoring', message);
    }
  }

  /**
   * Passed into the node binding to initiate outgoing connections
   * @param {PeerOffer} offer meta data of the peer to open a connection with
   */
  createOutgoingPeerConnection(offer) {
    log.trace('Offer ', offer);
    if (
      offer.to === this.node.nodeId ||
      offer.type === undefined ||
      !offer.from
    ) {
      log.error('Invalid peer connection call');
      this.pushMessage(NodeMessageTypes.PEER_RESPONSE, {
        from: offer.to,
        to: offer.from,
        type: offer.type,
        accepted: false,
        reason: 400 // Bad request
      });
      return;
    }
    if (this.peerConnections.has(offer.to.nodeId)) {
      log.warn(
        'Peer is already connected, ignoring call to create new connection'
      );
      return;
    }

    const peer = new Node(
      offer.to.tenantId,
      offer.to.contentId,
      offer.to.nodeId,
      offer.to.agentId
    );
    // waiting pings has a function to run after ping has been ACK'D
    this.waitingPings.set(offer.to.nodeId, response => {
      if (response.reason !== 200) {
        // If the offer was rejected, push the response to the kernel and don't bother making a connection
        this.pushMessage(NodeMessageTypes.PEER_RESPONSE, response);
        return;
      }
      // Only create the new connection if the response is accepted
      const connection = new PeerConnection(
        SignalConnection,
        this.cache,
        this.node,
        peer,
        this.peerConfig.rtcConfiguration,
        getConstraints(this.config.peering.webrtc),
        () => {
          // Only push the successful response to the kernel once the connection is established
          this.pushMessage(NodeMessageTypes.PEER_RESPONSE, response);
        },
        reason => {
          this._peerClosed(peer, reason);
        }
      );
      connection.setMessageHandler(MESSAGE_TYPES.NODE_MESSAGE, msg => {
        log.trace('Received message from peer: ', msg);
        this.pushMessage(msg.type, msg.body);
      });
      this.peerConnections.set(offer.to.nodeId, connection);
    });
    SignalConnection.sendPing(peer, this.node, offer);
    setTimeout(() => {
      if (this.waitingPings.has(offer.to.nodeId)) {
        log.trace(`Timing out PING from peer ${offer.to}`);
        const data = { closeEvent: { reason: 'timeout', code: 4003 } };
        sendConnectionEvent(ConnectionChangeState.ERROR, data);
        this.pushMessage(NodeMessageTypes.CLOSE_PEER, {
          from: offer.to.nodeId,
          to: this.node.nodeId,
          reason: AgentCodes.CONNECTION_TIMED_OUT // Timeout
        });
        this.waitingPings.delete(offer.to.nodeId);
      }
    }, 2000);
  }

  createIncomingPeerConnection(peer, incomingOffer) {
    if (peer.nodeId === this.node.nodeId) {
      log.error('getting offer from self');
      return;
    }
    if (!this.peerConnections.has(peer.nodeId)) {
      const chan = new PeerConnection(
        SignalConnection,
        this.cache,
        this.node,
        peer,
        this.peerConfig.rtcConfiguration,
        getConstraints(this.config.peering.webrtc),
        //open
        () => {
          this.pushMessage(NodeMessageTypes.PEER_READY, {
            peerId: peer.nodeId
          });
        },
        //close
        reason => {
          this._peerClosed(peer, reason);
        },
        incomingOffer
      );
      let clusterId = null;
      let depth = null;
      try {
        const attachment = this.kernelNode.getNode().attachment;
        if (attachment) {
          clusterId = attachment.activeCluster.id;
          depth = attachment.depth;
        }
      } catch (e) {
        log.warn('Unable to get kernel node info: ', e);
        clusterId = null;
      }
      if (incomingOffer) {
        chan.setMessageHandler(MESSAGE_TYPES.NODE_MESSAGE, msg => {
          log.trace('Received message from peer: ', msg);
          this.pushMessage(msg.type, msg.body);
        });
      }
      log.debug(`Unused depth: ${depth} and clusterId: ${clusterId}`);
      this.peerConnections.set(peer.nodeId, chan);
    } else {
      const chan = this.peerConnections.get(peer.nodeId);
      log.debug(`Restarting connection from ${peer.nodeId}`);
      const data = { message: `restart connection from ${peer.nodeId}` };
      sendConnectionEvent(ConnectionChangeState.RECONNECT, data);
      chan.restartConnection(chan);
    }
  }

  getPeerConnection(peer) {
    if (peer.nodeId === this.node.nodeId) {
      log.error('You cannot peer with yourself!');
      return null;
    }
    if (!this.peerConnections.has(peer.nodeId)) {
      log.info('connection not initialized');
    }
    return this.peerConnections.get(peer.nodeId);
  }

  /**
   * close based on an error in the system.
   */
  close(err) {
    log.warn('Closing down the Agent. error?:', err);
    const data = { error: err, closeEvent: { code: 4004 } };
    if (err && err.message) {
      data.message = err.message;
    }
    sendConnectionEvent(ConnectionChangeState.CLOSE, data);
    if (!this.isShutdown) {
      this.shutdown();
      this.onshutdown(err);
    }
  }

  /**
   * Shutdown the Agent. Clean up all resources and set initialized to false.
   */
  shutdown() {
    if (!this.isShutdown) {
      this.initialized = false;
      this.isShutdown = true;
      // Shutdown the kernel before the signal connection, in case there's some last minute messaging that
      // needs to happen
      try {
        if (this.kernelNode) {
          this.kernelNode.close();
          this.kernelNode = undefined;
        }
      } catch (e) {
        log.warn('BindingError: Could not call stop on kernel node', e);
      }
      SignalConnection.close();

      this.peerConnections.forEach(chan => {
        chan.close(
          new ChannelError('Agent is shutting down', AgentCodes.AGENT_SHUTDOWN)
        );
      });
      this.peerConnections.clear();
      this.cache.clear();
      this.unbindEvents();
    }
  }

  /**
   * Returns true if the url is a manifest url
   */
  isManifestUrl(url) {
    return (
      this.content.items &&
      this.content.items.length > 0 &&
      this.content.items.some(i => i.url === url)
    );
  }

  fetchInternal(request) {
    let timeout = this.kernelConfig.peerFetchTimeout;
    if (!request || !request.url || typeof request.url === 'undefined') {
      log.warn('Undefined content url');
      return Promise.reject({ unDeliverable: true });
    } else {
      const objectId = request.objectId || request.url;
      const metadata = request.url;
      const fetchPromise = new Promise((resolve, reject) => {
        // Save a callback for later, to handle when the response arrives at the agent
        this.fetchCallbacks.set(objectId, async body => {
          // body is a FetchResponse from the kernel
          const { peerId, reason, metadata } = body;
          // we know we can't serve it
          if (reason !== 200) {
            reject({
              unDeliverable: true,
              error: {
                statusCode: reason,
                message: `${reason}: Could not fetch object ${objectId}`
              },
              node: peer
            });
            return;
          }
          const cacheEntry = await this.cache.get(objectId);
          const peer = this.peerConnections.has(peerId)
            ? this.peerConnections.get(peerId).peer
            : null;
          if (
            !cacheEntry ||
            (!(cacheEntry instanceof ArrayBuffer) &&
              !(cacheEntry instanceof Uint8Array)) ||
            !cacheEntry.byteLength ||
            cacheEntry.byteLength <= 0
          ) {
            log.warn(
              `Invalid cache entry for object: ${objectId}, peerId: ${peerId}, status: ${reason}`,
              cacheEntry
            );
            reject({
              unDeliverable: false,
              error: {
                message: 'Invalid cache entry for ' + objectId,
                statusCode: AgentCodes.INVALID_CACHE_ENTRY
              },
              node: peer
            });
          } else {
            // copy the payload to a new array.
            let payload = cacheEntry.slice();
            // KollectiveXHR wants a uint8array instead of a plain ArrayBuffer, make sure to return one
            if (!(cacheEntry instanceof Uint8Array)) {
              payload = new Uint8Array(cacheEntry);
            }
            resolve({
              node: peer ? peer : this.node,
              data: { payload },
              metadata,
              ts: performance.now()
            });
          }
        });
        this.pushMessage(NodeMessageTypes.FETCH_REQUEST, {
          objectId,
          metadata
        });
      });
      // only use a timeout when the timeout value is valid and greater than 0	      return fetchPromise;
      if (!timeout || timeout === 0 || timeout > FETCH_TIMEOUT) {
        // TODO: This should be considered temporary until a better mechanism within the kernel can be defined
        timeout = FETCH_TIMEOUT;
      }
      if (timeout && timeout > 0) {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            if (this.fetchCallbacks.has(objectId)) {
              this.fetchCallbacks.delete(objectId);
              reject({
                error: {
                  message: 'timeout during fetch request',
                  statusCode: AgentCodes.FETCH_REQUEST_TIMEOUT
                },
                node: {},
                unDeliverable: false
              });
            }
          }, timeout);
        });
        return Promise.race([fetchPromise, timeoutPromise]);
      } else {
        return fetchPromise;
      }
    }
  }

  /**
   * Fetch an object from the peer cluster. the promise returns a peer response or errors with a RequestError
   * @param url URL string
   */
  fetchObject(request) {
    return this.fetchInternal(request);
    // TODO - Test seperately
    // return this.fetchInternal(url).then(
    //   res => {
    //     return res;
    //   },
    //   err => {
    //     if (this.retryTracker.shouldRetry()) {
    //       log.debug(`Retrying fetch for ${url} through kernel`);
    //       return this.fetchInternal(url);
    //     } else {
    //       return Promise.reject(err);
    //     }
    //   }
    // );
  }

  pushMessage(type, body) {
    if (this.kernelNode) {
      this.kernelNode.pushMessage(type, body);
    }
  }

  /**
   * When an object is pulled from cdn or origin, we put in the in the peer manager to be cached.
   * @param objectId string object id for data
   * @param data {!Object} Object containing a payload represented as an ArrayBuffer
   */
  putObject(objectId, data) {
    if (!this.initialized) {
      log.trace(
        `putObject called on ${objectId} before initialization is complete`
      );
      return;
    }
    // TODO: Handle other members of data, like mime
    const { payload } = data;
    if (payload && payload.byteLength !== undefined && payload.byteLength > 0) {
      const alreadyInCache = this.cache.has(objectId);
      this.cache.set(objectId, payload).then(didSet => {
        if (!alreadyInCache && didSet) {
          this.pushMessage(NodeMessageTypes.AGENT_EVENT, {
            type: AgentEventType.SOURCED,
            objectId
          });
        }
      });
    } else {
      log.warn(
        'Agent received payload that was not an ArrayBuffer, or was length 0',
        objectId,
        data
      );
    }
  }

  /**
   * Notify the cluster that the url is being sourced from cdn or origin
   */
  notifyPending(objectId) {
    this.pushMessage(NodeMessageTypes.AGENT_EVENT, {
      type: AgentEventType.SOURCING,
      objectId
    });
  }

  /**
   * Notify the cluster that the url is being sourced from cdn or origin
   */
  notifyEvicted(objectId) {
    this.pushMessage(NodeMessageTypes.AGENT_EVENT, {
      type: AgentEventType.EVICTED,
      objectId
    });
  }

  /**
   * @returns {Node} node metadata plus the attachment
   */
  getNode() {
    let knode = {};
    try {
      if (this.kernelNode) {
        knode = this.kernelNode.getNode();
      } else {
        log.info('getNode called before kernelNode is set');
      }
    } catch (e) {
      log.warn('Error: Could not get node', e);
    }
    const attachment = knode.attachment ? knode.attachment : {};
    const cluster = attachment.activeCluster ? attachment.activeCluster : {};
    const clusterId = cluster.id ? cluster.id : PENDING_CLUSTER;
    return { ...this.node, clusterId };
  }

  /**
   * In the case that the agent is not initialized, we will log transfers here. See SRV-735.
   */
  sendTransferRecord(record) {
    this.eventEmitter.emit(InternalEvents.TRANSFER_RECORD, { data: record });
  }
}
